<template>
  <v-col class="register default-slot" md="7" sm="12">
    <add-co-owner-modal
      :changeCoOwnerInfo="changeCoOwnerInfo"
      :coOwnerDialog="coOwnerDialog"
      :toggleCoOwnerModal="toggleCoOwnerModal"
      :coOwnerInfo="coOwnerInfo"
    />
    <v-dialog v-model="rulesDialog">
      <v-card class="search-modal">
        <h2>Rules</h2>
        <ol class="text-left">
          <li>
            Applicant(s) must have been a Full (Single/Dual/Lifetime) ASCA
            member(s) in good standing for five (5) previous consecutive years.
            Applicant(s) must be 18 years of age or older.
          </li>
          <li>
            Kennel Names registered from 1971 to 1/15/76 are a lifetime issue
            and may not be reassigned except by transfer. There are no further
            kennel renewal fees unless the kennel name is transferred.
          </li>
          <li>
            Kennel Names registered from 1/16/76 to present must be renewed
            every three years. Kennel name will be considered abandoned if
            renewal payment is not postmarked on or before the expiration date.
            The original owner of the kennel name may renew or transfer the
            abandoned kennel name for the first five years following abandonment
            of the kennel name, but after the five years has passed, anyone can
            register the kennel name as their own (effective 1/1/02).
          </li>
          <li>
            All owners or partners of said Kennel Name must sign application for
            Kennel Name to be included on certificate and registry office file
            pertaining to same. Once the Kennel Name is accepted and registered
            with ASCA, no other breeder or owner will be permitted to use the
            Kennel Name or sound alike names without written permission of
            recorded owner(s) of said Kennel Name when the Kennel Name is placed
            at the beginning of a dog's name or at the end when it is preceded
            by the word "of" (1/1/02).
          </li>
          <li>
            Dog's names are limited to thirty (30) letters including spaces,
            punctuation and Kennel Name as stated under Individual Dog
            Registrations. It is suggested that the kennel name not contain the
            words Aussies, Kennels, Farms, etc as the entire kennel name is
            required to be used when naming the dog.
          </li>
          <li>
            A Kennel Name will be recognized only if it is registered with ASCA.
          </li>
          <li>
            A fee will be required to make any deletions, additions, or
            transfers in ownership of a Kennel Name registration.
          </li>
          <li>No variations in spelling or sound-a-likes will be allowed.</li>
          <li>No color used independently will be allowed.</li>
          <li>
            A Kennel Name application and the registration fee must be submitted
            to the ASCA Business Office when either the initial or renewal
            application is made.
          </li>
          <li>
            After 5 renewals, a total of fifteen (15) years, a Kennel Name is
            permanent.
          </li>
          <li>No offensive or profane language will be permitted.</li>
          <li>
            No new Kennel Name may contain the exact name of an existing
            registered kennel.
          </li>
          <li>
            Renewal applications must be submitted by a present member in good
            standing with ASCA.
          </li>
          <li>
            The Business Office will list kennel names in the Aussie Times with
            a notation to send objections and the reason for the objection to
            the Business Office for consideration.
          </li>
          <li>
            Kennel names should not include the words "Aussies, Australian
            Shepherds, Kennels".
          </li>
          <li>
            Kennel name second choice cannot be a pluralized version of the
            first one - i.e. first choice ending with an s or 's.
          </li>
        </ol>
      </v-card>
    </v-dialog>
    <div class="top-icons">
      <router-link class="back-icon" to="/">
        <font-awesome-icon icon="long-arrow-alt-left" size="lg"/> Back to home
      </router-link>
    </div>
    <h1 class="register-title">Kennel Name Registration Application</h1>
    <p class="mt-3">
      <a v-on:click="toggleRules()">View rules</a> <br />
      <a
        class="no-text-decoration"
        href="https://docs.google.com/document/d/1yp9zVV7dA_IvfmXEmW85QV39XVZbX8OZ0ncsK0Vg-0w/edit?tab=t.0"
        target="_blank"
      >
        View Current Kennel Names
      </a>
    </p>
    <h1>${{ price }}</h1>
    <p>Review the rules and existing kennel names before selecting your first and second choices.</p>
    <v-alert type="warning" outlined v-if="kennelNameChoices.firstChoice.length >= 20 || kennelNameChoices.secondChoice.length >= 20">
      We recommend keeping kennel names concise (20 characters or less), so it can be included as a prefix or suffix in dogs names.
      <br/> The maximum length of the dog's name is 50 characters, including 1 or 2 kennels names.
    </v-alert>
    <v-alert type="warning" outlined v-if="!!firstChoiceErrorMessage">
      First choice "<strong>{{ kennelNameChoices.firstChoice }}</strong>": {{ firstChoiceErrorMessage }}
    </v-alert>
    <v-alert type="warning" outlined v-if="!!secondChoiceErrorMessage">
      Second choice "<strong>{{ kennelNameChoices.secondChoice }}</strong>": {{ secondChoiceErrorMessage }}
    </v-alert>
    <v-form v-if="userInfo && userInfo.fullYearsOfActiveMembership >= 5" class="register-form" v-model="isValid">
      <v-text-field
        outlined
        label="First Choice"
        v-model="kennelNameChoices.firstChoice"
        :rules="[
          (v) => !!v || 'First Choice is required',
          (v) => v.length < 50 || '(50) characters max'          
        ]"
      ></v-text-field>
      <v-text-field
        outlined
        v-model="kennelNameChoices.secondChoice"
        label="Second Choice"
        :rules="[
          (v) => !!v || 'Second Choice is required',
          (v) => v.length < 50 || '(50) characters max'
        ]"
      ></v-text-field>
      <v-row>
        <v-col>
          <h3>Primary Owner:</h3>
          <div v-if="!!userInfo">
            <v-tooltip dark right>
              <template v-slot:activator="{ on, attrs }">
                <font-awesome-icon
                  icon="paw"
                  :color="'blue'"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <span>
                {{"The primary kennel owner is the main contact person for the ASCA office."}}<br/>
                {{"Only they can initiate kennel ownership transfer in the future."}}
              </span>
            </v-tooltip>
            <span>
              {{ userInfo.firstName }} {{ userInfo.lastName }}
              <span v-if="!!userInfo.memberId">
                (Member: {{ userInfo.memberId }})
              </span>
            </span>
          </div>
        </v-col>
        <v-col>
          <h3>
            Co-Owners:
            <v-badge
              v-if="coOwnerInfo.length > 0"
              :content="coOwnerInfo.length"
              offset-y="-5"
            />
          </h3>
          <div v-if="coOwnerInfo[0]">
            <span>
              {{ coOwnerInfo[0].firstName }} {{ coOwnerInfo[0].lastName }}
              <span v-if="!!coOwnerInfo[0].memberId">
                (Member: {{ coOwnerInfo[0].memberId }})
              </span>
            </span>
            <a v-on:click="toggleCoOwnerModal()">Edit</a>
          </div>
          <div v-else>
            <a v-on:click="toggleCoOwnerModal()">
              <font-awesome-icon icon="plus" />
              Add co-owner
            </a>
          </div>
        </v-col>
      </v-row>
      <signature class="mt-5" :signature="sig" v-on:update="sig = $event">
        <template v-slot:signatureText>
          <kennel-signature-text />
        </template>
      </signature>  
    </v-form>
    <p v-else>
      You have not been active for 5 full years, so you cannot register a kennel.
    </p>

    <v-btn
      class="white--text"
      large
      v-on:click="
        changeKennelNames(kennelNameChoices.firstChoice, kennelNameChoices.secondChoice);
        updateSignature(sig);
        changePage(2);
      "
      :disabled="!isValid || !sig || price === null || firstChoiceLookupFailed || secondChoiceLookupFailed"
      color="primary"
      >NEXT</v-btn
    >
    <p class="steps">STEP <b>1</b> OF <b>2</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";
import Signature from "@/components/common/inputs/Signature.vue";
import AddCoOwnerModal from "@/components/Portal/AddCoOwnerModal_Kennel.vue";
import KennelSignatureText from "@/components/common/KennelSignatureText.vue";
import FooterText from "@/components/common/FooterText.vue";

import { UserInfo } from "@/types/User";
import registerKennelService from "@/services/portal/registerKennelService";

export default Vue.extend({
  name: "RegisterKennelForm1",
  components: {
    AddCoOwnerModal,
    Signature,
    KennelSignatureText,
    FooterText,
  },
  data: function () {
    return {
      firstChoiceMatch: null,
      kennelNameChoices: { ...this.kennelNames },
      sig: this.$props.signature,
      secondChoiceMatch: null,
      firstChoiceLookupFailed: false,
      secondChoiceLookupFailed: false,
      isValid: true,
      coOwnerDialog: false,
      rulesDialog: false,
    };
  },
  methods: {
    toggleCoOwnerModal: function () {
      this.$data.coOwnerDialog = !this.$data.coOwnerDialog;
    },
    toggleRules: function () {
      this.$data.rulesDialog = !this.$data.rulesDialog;
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    firstChoiceErrorMessage(): string | null {
      return this.firstChoiceMatch
        ? `The name will be approved after verification by the ASCA Office. Please, review the rules and ensure the name is not too similar to the following existing kennel(s): ${this.firstChoiceMatch}.`
        : null;
    },
    secondChoiceErrorMessage(): string | null {
      return this.secondChoiceMatch
        ? `The name will be approved after verification by the ASCA Office. Please, review the rules and ensure the name is not too similar to the following existing kennel(s): ${this.secondChoiceMatch}.`
        : null;
    },
  },
  created() {},
  watch: {
    "kennelNameChoices.firstChoice": {
      handler: debounce(
        500,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal) return;
          if (newVal) {
            this.firstChoiceLookupFailed = true;
            registerKennelService
              .searchKennelName(newVal)
              .then((response: String[]) => {
                if (this.kennelNameChoices.firstChoice !== newVal) return;

                if (response.length > 0) {
                  this.firstChoiceMatch = response.join(", ");
                } else {
                  this.firstChoiceMatch = null;
                }
                this.firstChoiceLookupFailed = false;
              })
              .catch((error) => {
                if (this.kennelNameChoices.firstChoice === newVal) this.$handleError(error);
              });
          } else {
            this.firstChoiceMatch = null;
            this.firstChoiceLookupFailed = false;
          }
        }
      ),
      deep: true,
    },
    "kennelNameChoices.secondChoice": {
      handler: debounce(
        500,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal) return;
          if (newVal) {
            this.secondChoiceLookupFailed = true;
            registerKennelService
              .searchKennelName(newVal)
              .then((response: String[]) => {
                if (this.kennelNameChoices.secondChoice !== newVal) return;

                if (response.length > 0) {
                  this.secondChoiceMatch = response.join(", ");
                } else {
                  this.secondChoiceMatch = null;
                }
                this.secondChoiceLookupFailed = false;
              })
              .catch((error) => {
                if (this.kennelNameChoices.secondChoice === newVal) this.$handleError(error);
              });
          } else {
            this.secondChoiceMatch = null;
            this.secondChoiceLookupFailed = false;
          }
        }
      ),
      deep: true,
    },
  },
  props: {
    changePage: Function,
    searchMemberInfo: Object,
    changeCoOwnerInfo: Function,
    coOwnerInfo: Array,
    changeKennelNames: Function,
    updateSignature: Function,
    signature: String,
    kennelNames: Object,
    price: Number,
  },
});
</script>
